/**
 * Confusions classiques de la reconnaissance d'image, repliées vers le caractère probable.
 * Deux tables distinctes : `B` est un chiffre hexadécimal légitime dans un Epic ID, le replier
 * vers 8 corromprait une valeur juste — il n'est replié que sur la voie purement numérique.
 */
const FOLD_HEX: Record<string, string> = {
  O: "0", o: "0", I: "1", l: "1", i: "1", S: "5", s: "5", Z: "2", z: "2", g: "9", q: "9", G: "6",
};
const FOLD_DIGITS: Record<string, string> = {
  ...FOLD_HEX, B: "8", b: "8", D: "0", T: "7",
};

/**
 * Isole les identifiants d'un texte brut — cellules copiées depuis Google Sheets ou résultat de
 * la lecture d'une capture d'écran. Seules deux formes sont retenues : un Epic ID (32 caractères
 * hexadécimaux) et un Discord ID (15 à 21 chiffres). Tout le reste, pseudos compris, est ignoré :
 * un pseudo lu de travers ne doit jamais servir à retrouver une ligne.
 *
 * Un jeton presque conforme est réessayé après repli des confusions de lecture : sans cela, un
 * seul `O` lu à la place d'un `0` faisait disparaître l'identifiant sans même un signalement.
 * La correction reste sans danger : le jeton replié doit encore correspondre exactement à une
 * ligne de la base pour avoir un effet.
 */
export function extractIdentifiers(text: string): { epicIds: string[]; discordIds: string[] } {
  const epicIds = new Set<string>();
  const discordIds = new Set<string>();
  // Le découpage par caractère non alphanumérique évite qu'une lecture d'image ne recolle deux
  // colonnes voisines en un seul faux identifiant : les bordures de cellules deviennent des
  // séparateurs (espaces, barres, ponctuation).
  for (const token of text.split(/[^0-9a-zA-Z]+/)) {
    if (/^[0-9a-fA-F]{32}$/.test(token)) { epicIds.add(token.toLowerCase()); continue; }
    if (/^\d{15,21}$/.test(token)) { discordIds.add(token); continue; }
    const hexFold = token.replace(/[OoIliSsZzgqG]/g, (c) => FOLD_HEX[c]);
    if (/^[0-9a-fA-F]{32}$/.test(hexFold)) { epicIds.add(hexFold.toLowerCase()); continue; }
    const digitFold = token.replace(/[OoIliSsZzgqGBbDT]/g, (c) => FOLD_DIGITS[c]);
    if (/^\d{15,21}$/.test(digitFold)) discordIds.add(digitFold);
  }
  // Borne alignée sur celle du serveur : une capture d'écran n'en contient jamais autant.
  return { epicIds: [...epicIds].slice(0, 100), discordIds: [...discordIds].slice(0, 100) };
}
